import type {
  FieldErrorCode,
  PublicContextualValidator,
  PublicFieldValidator,
  PublicFieldVisibilityState,
  PublicFormSchemaResponse,
  PublicScalarField,
  PublicSchemaNode,
  PublicValidationRule,
  PublicVisibilityRule,
  SubmitPayload,
  SubmitResponse,
  ValidateResponse,
  ValidationFailedError,
  ValidationIssue,
} from "./types.js";

export type PublicSchemaInput = PublicFormSchemaResponse | Record<string, PublicSchemaNode>;

export interface PublicSchemaFieldEntry {
  path: string;
  key: string;
  parentPath?: string;
  depth: number;
  repeated: boolean;
  node: PublicSchemaNode;
}

export interface FormerieFieldCollectionOptions {
  schema?: PublicSchemaInput;
  includeMissingCheckboxes?: boolean;
}

export interface CreateFormerieSubmitPayloadOptions extends FormerieFieldCollectionOptions {
  metadata?: Record<string, unknown>;
  turnstileToken?: string;
  verificationToken?: string;
}

export interface ApplyFormerieValidationErrorsOptions {
  clearExisting?: boolean;
  errorAttribute?: string;
  errorForAttribute?: string;
  fieldAttribute?: string;
  invalidAttribute?: string;
}

export interface ValidateFormeriePublicFieldsOptions {
  mode?: "final" | "partial" | "step";
  paths?: string[];
  step?: string;
}

export interface FormeriePublicFieldsValidationResult {
  ok: boolean;
  values: Record<string, unknown>;
  errors: Record<string, ValidationIssue[]>;
  issues: ValidationIssue[];
}

type FieldConditionOperator =
  | "isFilled"
  | "isEmpty"
  | "equals"
  | "notEquals"
  | "in"
  | "notIn"
  | "greaterThan"
  | "greaterThanOrEqual"
  | "lessThan"
  | "lessThanOrEqual"
  | "matches";

interface FieldCondition extends Record<string, unknown> {
  path: string;
  operator: FieldConditionOperator;
  value?: unknown;
}

const DEFAULT_FIELD_ATTRIBUTE = "data-formerie-field";
const DEFAULT_ERROR_ATTRIBUTE = "data-formerie-error";
const DEFAULT_ERROR_FOR_ATTRIBUTE = "data-formerie-error-for";
const DEFAULT_ERROR_VISIBLE_ATTRIBUTE = "data-formerie-error-visible";
const DEFAULT_INVALID_ATTRIBUTE = "data-formerie-invalid";

const FIELD_CONDITION_OPERATORS = new Set<FieldConditionOperator>([
  "equals",
  "greaterThan",
  "greaterThanOrEqual",
  "in",
  "isEmpty",
  "isFilled",
  "lessThan",
  "lessThanOrEqual",
  "matches",
  "notEquals",
  "notIn",
]);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_PHONE_PATTERN = /^\+[1-9]\d{1,14}$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATETIME_WITH_ZONE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/;
const TIME_HH_MM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_HH_MM_SS_PATTERN = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
const CA_POSTAL_CODE_PATTERN = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ -]?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i;
const US_POSTAL_CODE_PATTERN = /^\d{5}(?:-\d{4})?$/;
const DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const IPV4_PATTERN = /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const UUID_ANY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const UUID_V7_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DEFAULT_URL_PROTOCOLS = ["http:", "https:"];
const CA_REGION_CODES = new Set(["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"]);
const US_REGION_CODES = new Set([
  "AL", "AK", "AS", "AZ", "AR", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "GU", "HI", "IA", "ID",
  "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MP", "MS", "MT", "NC", "ND",
  "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX",
  "UT", "VA", "VI", "VT", "WA", "WI", "WV", "WY",
]);

export function listPublicSchemaFields(schema: PublicSchemaInput): PublicSchemaFieldEntry[] {
  const entries: PublicSchemaFieldEntry[] = [];

  for (const [key, node] of Object.entries(schemaFields(schema))) {
    visitSchemaNode(entries, node, key, key, undefined, 0, false);
  }

  return entries;
}

export function getPublicSchemaField(
  schema: PublicSchemaInput,
  path: string,
): PublicSchemaNode | undefined {
  const normalizedPath = normalizePublicSchemaPath(path);

  return listPublicSchemaFields(schema).find((entry) =>
    entry.path === path || entry.path === normalizedPath,
  )?.node;
}

export function visiblePublicSchemaFields(
  schema: PublicSchemaInput,
  values: Record<string, unknown>,
): PublicSchemaFieldEntry[] {
  const hiddenPaths = resolveHiddenPublicSchemaPaths(schema, values);

  return listPublicSchemaFields(schema).filter((entry) =>
    !isNormalizedPathHidden(entry.path, hiddenPaths),
  );
}

export function evaluatePublicFieldVisibility(
  schema: PublicSchemaInput,
  values: Record<string, unknown>,
): Record<string, PublicFieldVisibilityState> {
  const hiddenPaths = resolveHiddenPublicSchemaPaths(schema, values);

  return Object.fromEntries(
    listPublicSchemaFields(schema).map((entry) => [
      entry.path,
      isNormalizedPathHidden(entry.path, hiddenPaths) ? "hidden" : "visible",
    ]),
  );
}

export function getPublicFieldVisibility(
  schema: PublicSchemaInput,
  values: Record<string, unknown>,
  path: string,
): PublicFieldVisibilityState {
  return isPublicSchemaPathHidden(schema, values, path) ? "hidden" : "visible";
}

export function isPublicSchemaPathHidden(
  schema: PublicSchemaInput,
  values: Record<string, unknown>,
  path: string,
): boolean {
  return isNormalizedPathHidden(path, resolveHiddenPublicSchemaPaths(schema, values));
}

export function resolveHiddenPublicSchemaPaths(
  schema: PublicSchemaInput,
  values: Record<string, unknown>,
): Set<string> {
  const hiddenPaths = new Set<string>();
  const source = schemaVisibilityRules(schema);

  for (const rule of source) {
    if (!isRuntimeVisibilityRule(rule)) {
      continue;
    }

    const defaultState = rule.default ?? "visible";
    const state = safeEvaluatePublicCondition(rule.when, values) ? rule.state : defaultState;

    if (state === "hidden") {
      hiddenPaths.add(normalizePublicSchemaPath(rule.path));
    }
  }

  return hiddenPaths;
}

export function evaluatePublicCondition(
  condition: unknown,
  values: Record<string, unknown>,
): boolean {
  if (!isRecord(condition)) {
    return false;
  }

  if ("all" in condition) {
    return Array.isArray(condition.all) &&
      condition.all.length > 0 &&
      condition.all.every((child) => evaluatePublicCondition(child, values));
  }

  if ("any" in condition) {
    return Array.isArray(condition.any) &&
      condition.any.length > 0 &&
      condition.any.some((child) => evaluatePublicCondition(child, values));
  }

  if ("not" in condition) {
    if (!isPublicConditionExpression(condition.not)) {
      return false;
    }

    return evaluatePublicCondition(condition.not, values) === false;
  }

  if (!isFieldCondition(condition)) {
    return false;
  }

  return evaluatePublicFieldCondition(condition, values);
}

export function resolveFormerieFieldValue(
  values: Record<string, unknown>,
  path: string,
): unknown {
  if (Object.prototype.hasOwnProperty.call(values, path)) {
    return values[path];
  }

  const tokens = publicPathTokens(path);
  if (tokens.length === 0) {
    return undefined;
  }

  return resolvePathTokens(values, tokens, 0);
}

export function formDataToFormerieFields(
  formData: FormData,
  options: FormerieFieldCollectionOptions = {},
): Record<string, unknown> {
  const fields: Record<string, unknown> = {};

  formData.forEach((entryValue, path) => {
    if (!path) {
      return;
    }

    const schemaNode = schemaFieldForInput(options.schema, path);
    const value = coerceFormDataEntry(entryValue, schemaNode);
    setFormerieFieldValue(fields, path, value);
  });

  applyMissingCheckboxValues(fields, options);

  return fields;
}

export function collectFormerieFormFields(
  form: HTMLFormElement,
  options: FormerieFieldCollectionOptions = {},
): Record<string, unknown> {
  return formDataToFormerieFields(new FormData(form), options);
}

export function setFormerieFieldValue(
  fields: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const tokens = publicPathTokens(path);

  if (tokens.length === 0) {
    return fields;
  }

  setPathTokenValue(fields, tokens, value);
  return fields;
}

export function createFormerieSubmitPayload(
  source: FormData | HTMLFormElement | Record<string, unknown>,
  options: CreateFormerieSubmitPayloadOptions = {},
): SubmitPayload {
  const fields = isFormData(source)
    ? formDataToFormerieFields(source, options)
    : isHtmlFormElement(source)
      ? collectFormerieFormFields(source, options)
      : source;

  return withoutUndefined({
    fields,
    metadata: options.metadata,
    turnstileToken: options.turnstileToken,
    verificationToken: options.verificationToken,
  });
}

export function validateFormeriePublicFields(
  schema: PublicSchemaInput,
  submittedFields: unknown,
  options: ValidateFormeriePublicFieldsOptions = {},
): FormeriePublicFieldsValidationResult {
  const input = isRecord(submittedFields) ? submittedFields : {};
  const selectedPaths = selectedPublicValidationPaths(schema, input, options);
  const hiddenPaths = resolveHiddenPublicSchemaPaths(schema, input);
  const issues: ValidationIssue[] = [];
  const values: Record<string, unknown> = {};

  for (const entry of listPublicSchemaFields(schema)) {
    if (
      entry.repeated ||
      entry.node.type === "object" ||
      !publicValidationShouldVisit(entry.path, selectedPaths, hiddenPaths)
    ) {
      continue;
    }

    const rawValue = resolveFormerieFieldValue(input, entry.path);
    const result = validatePublicSchemaNodeValue(rawValue, entry.node, entry.path);

    issues.push(...result.issues);

    if (result.includeValue) {
      setFormerieFieldValue(values, entry.path, result.value);
    }
  }

  issues.push(...validatePublicContextualRules(schema, input, issues, selectedPaths, hiddenPaths));

  return {
    ok: issues.length === 0,
    values,
    errors: groupValidationIssuesByPath(issues),
    issues,
  };
}

export function validationIssuesByField(
  source: SubmitResponse | ValidateResponse | ValidationFailedError | unknown,
): Record<string, ValidationIssue[]> {
  const error = validationFailedErrorFrom(source);
  return error?.fields ?? {};
}

export function validationMessagesByField(
  source: SubmitResponse | ValidateResponse | ValidationFailedError | unknown,
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(validationIssuesByField(source)).map(([path, issues]) => [
      path,
      issues.map((issue) => issue.message),
    ]),
  );
}

export function validationMessagesForField(
  source: SubmitResponse | ValidateResponse | ValidationFailedError | unknown,
  path: string,
): string[] {
  return validationMessagesByField(source)[path] ?? [];
}

export function firstValidationMessageForField(
  source: SubmitResponse | ValidateResponse | ValidationFailedError | unknown,
  path: string,
): string | null {
  return validationMessagesForField(source, path)[0] ?? null;
}

export function clearFormerieValidationErrors(
  root: ParentNode,
  options: ApplyFormerieValidationErrorsOptions = {},
): void {
  const errorAttribute = safeAttributeName(options.errorAttribute, DEFAULT_ERROR_ATTRIBUTE);
  const errorForAttribute = safeAttributeName(options.errorForAttribute, DEFAULT_ERROR_FOR_ATTRIBUTE);
  const invalidAttribute = safeAttributeName(options.invalidAttribute, DEFAULT_INVALID_ATTRIBUTE);

  for (const element of queryAll(root, attributePresenceSelector(invalidAttribute))) {
    element.removeAttribute(invalidAttribute);
    element.removeAttribute(errorAttribute);

    if (element.getAttribute("aria-invalid") === "true") {
      element.removeAttribute("aria-invalid");
    }
  }

  for (const element of queryAll(root, attributePresenceSelector(errorForAttribute))) {
    element.textContent = "";
    if (element.getAttribute(DEFAULT_ERROR_VISIBLE_ATTRIBUTE) === "true") {
      element.setAttribute("hidden", "");
      element.removeAttribute(DEFAULT_ERROR_VISIBLE_ATTRIBUTE);
    }
  }
}

export function applyFormerieValidationErrors(
  root: ParentNode,
  source: SubmitResponse | ValidateResponse | ValidationFailedError | unknown,
  options: ApplyFormerieValidationErrorsOptions = {},
): string[] {
  const errorAttribute = safeAttributeName(options.errorAttribute, DEFAULT_ERROR_ATTRIBUTE);
  const errorForAttribute = safeAttributeName(options.errorForAttribute, DEFAULT_ERROR_FOR_ATTRIBUTE);
  const fieldAttribute = safeAttributeName(options.fieldAttribute, DEFAULT_FIELD_ATTRIBUTE);
  const invalidAttribute = safeAttributeName(options.invalidAttribute, DEFAULT_INVALID_ATTRIBUTE);

  if (options.clearExisting !== false) {
    clearFormerieValidationErrors(root, options);
  }

  const applied: string[] = [];

  for (const [path, messages] of Object.entries(validationMessagesByField(source))) {
    if (messages.length === 0) {
      continue;
    }

    const message = messages.join("\n");
    const fieldSelector = [
      attributeValueSelector("name", path),
      attributeValueSelector(fieldAttribute, path),
    ].join(", ");

    for (const element of queryAll(root, fieldSelector)) {
      element.setAttribute("aria-invalid", "true");
      element.setAttribute(invalidAttribute, "true");
      element.setAttribute(errorAttribute, message);
    }

    for (const element of queryAll(root, attributeValueSelector(errorForAttribute, path))) {
      element.textContent = message;
      if (element.hasAttribute("hidden")) {
        element.removeAttribute("hidden");
        element.setAttribute(DEFAULT_ERROR_VISIBLE_ATTRIBUTE, "true");
      }
    }

    applied.push(path);
  }

  return applied;
}

interface PublicNodeValidationResult {
  includeValue: boolean;
  value?: unknown;
  issues: ValidationIssue[];
}

function validatePublicSchemaNodeValue(
  rawValue: unknown,
  node: PublicSchemaNode,
  path: string,
): PublicNodeValidationResult {
  if (node.type === "array") {
    return validatePublicArrayValue(rawValue, node, path);
  }

  if (node.type === "object") {
    return {
      includeValue: isRecord(rawValue),
      value: rawValue,
      issues: [],
    };
  }

  const value = coercePublicValidationValue(rawValue, node);

  if (isEmptyValue(value)) {
    return {
      includeValue: rawValue !== undefined,
      value,
      issues: node.required === true
        ? [publicValidationIssue(path, "FIELD_REQUIRED", "required", "This field is required.")]
        : [],
    };
  }

  return {
    includeValue: true,
    value,
    issues: validatePublicScalarValue(value, node, path),
  };
}

function validatePublicArrayValue(
  rawValue: unknown,
  node: Extract<PublicSchemaNode, { type: "array" }>,
  path: string,
): PublicNodeValidationResult {
  if (isEmptyValue(rawValue)) {
    return {
      includeValue: rawValue !== undefined,
      value: rawValue,
      issues: node.required === true
        ? [publicValidationIssue(path, "FIELD_REQUIRED", "required", "This field is required.")]
        : [],
    };
  }

  if (!Array.isArray(rawValue)) {
    return {
      includeValue: false,
      issues: [publicValidationIssue(path, "FIELD_TYPE_INVALID", "type", "This field must be an array.")],
    };
  }

  const issues: ValidationIssue[] = [];

  if (typeof node.minItems === "number" && rawValue.length < node.minItems) {
    issues.push(publicValidationIssue(path, "FIELD_ARRAY_TOO_SHORT", "minItems", `Must include at least ${node.minItems} items.`, {
      minItems: node.minItems,
    }));
  }

  if (typeof node.maxItems === "number" && rawValue.length > node.maxItems) {
    issues.push(publicValidationIssue(path, "FIELD_ARRAY_TOO_LONG", "maxItems", `Must include at most ${node.maxItems} items.`, {
      maxItems: node.maxItems,
    }));
  }

  if (node.uniqueItems === true && new Set(rawValue.map((entry) => JSON.stringify(entry))).size !== rawValue.length) {
    issues.push(publicValidationIssue(path, "FIELD_ARRAY_DUPLICATES", "uniqueItems", "This field contains duplicate items."));
  }

  return {
    includeValue: true,
    value: rawValue,
    issues,
  };
}

function validatePublicScalarValue(value: unknown, field: PublicScalarField, path: string): ValidationIssue[] {
  switch (field.type) {
    case "number":
      return validatePublicNumberValue(value, field, path);
    case "boolean":
      return validatePublicBooleanValue(value, field, path);
    case "checkbox":
      return validatePublicCheckboxValue(value, field, path);
    case "date":
    case "email":
    case "phone":
    case "select":
    case "string":
    case "textarea":
    case "url":
      return validatePublicStringLikeValue(value, field, path);
  }
}

function validatePublicStringLikeValue(value: unknown, field: PublicScalarField, path: string): ValidationIssue[] {
  if (typeof value !== "string") {
    return [publicValidationIssue(path, "FIELD_TYPE_INVALID", "type", "This field must be text.")];
  }

  const issues: ValidationIssue[] = [];

  if (typeof field.exactLength === "number" && value.length !== field.exactLength) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_LENGTH_INVALID", "exactLength", `Must be exactly ${field.exactLength} characters.`, {
      exactLength: field.exactLength,
    }));
  }

  if (field.exactLength === undefined && typeof field.min === "number" && value.length < field.min) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_TOO_SHORT", "min", `Must be at least ${field.min} characters.`, {
      min: field.min,
    }));
  }

  if (field.exactLength === undefined && typeof field.max === "number" && value.length > field.max) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_TOO_LONG", "max", `Must be at most ${field.max} characters.`, {
      max: field.max,
    }));
  }

  if (field.uuidVersion !== undefined && !uuidPattern(field.uuidVersion).test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_UUID_INVALID", "uuidVersion", "Invalid UUID.", {
      uuidVersion: field.uuidVersion,
    }));
  }

  if (field.countryCode !== undefined && !/^[A-Z]{2}$/i.test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_COUNTRY_CODE_INVALID", "countryCode", "Invalid country code.", {
      countryCode: field.countryCode,
    }));
  }

  if (field.postalCodeCountry !== undefined && !postalCodePattern(field.postalCodeCountry).test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_POSTAL_CODE_INVALID", "postalCodeCountry", "Invalid postal code.", {
      postalCodeCountry: field.postalCodeCountry,
    }));
  }

  if (field.regionCodeCountry !== undefined && !regionCodes(field.regionCodeCountry).has(value.toUpperCase())) {
    issues.push(publicValidationIssue(path, "FIELD_REGION_CODE_INVALID", "regionCodeCountry", "Invalid region code.", {
      regionCodeCountry: field.regionCodeCountry,
    }));
  }

  if (field.timeFormat !== undefined && !(field.timeFormat === "HH:mm:ss" ? TIME_HH_MM_SS_PATTERN : TIME_HH_MM_PATTERN).test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_TIME_INVALID", "timeFormat", "Invalid time.", {
      timeFormat: field.timeFormat,
    }));
  }

  if (field.dateTimeFormat !== undefined && !ISO_DATETIME_WITH_ZONE_PATTERN.test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_DATETIME_INVALID", "dateTimeFormat", "Invalid date-time.", {
      dateTimeFormat: field.dateTimeFormat,
    }));
  }

  if (field.startsWith !== undefined && !value.startsWith(field.startsWith)) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_PREFIX_INVALID", "startsWith", `Must start with ${field.startsWith}.`, {
      startsWith: field.startsWith,
    }));
  }

  if (field.endsWith !== undefined && !value.endsWith(field.endsWith)) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_SUFFIX_INVALID", "endsWith", `Must end with ${field.endsWith}.`, {
      endsWith: field.endsWith,
    }));
  }

  if (field.contains !== undefined && !value.includes(field.contains)) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_CONTAINS_REQUIRED", "contains", `Must contain ${field.contains}.`, {
      contains: field.contains,
    }));
  }

  if (field.notContains !== undefined && value.includes(field.notContains)) {
    issues.push(publicValidationIssue(path, "FIELD_STRING_CONTAINS_FORBIDDEN", "notContains", `Must not contain ${field.notContains}.`, {
      notContains: field.notContains,
    }));
  }

  if (field.allowedValues && !field.allowedValues.includes(value)) {
    issues.push(publicValidationIssue(path, "FIELD_VALUE_NOT_ALLOWED", "allowedValues", "This field contains an unsupported value.", {
      allowedValues: field.allowedValues,
    }));
  }

  if (field.type === "email" && !EMAIL_PATTERN.test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_EMAIL_INVALID", "email", "Invalid email address."));
  }

  if (field.type === "phone" && field.phoneFormat === "e164" && !E164_PHONE_PATTERN.test(value)) {
    issues.push(publicValidationIssue(path, "FIELD_PHONE_INVALID", "phoneFormat", "Invalid phone number.", {
      phoneFormat: field.phoneFormat,
    }));
  }

  if (field.type === "url" && !publicUrlIsValid(value, field.allowedProtocols)) {
    issues.push(publicValidationIssue(path, "FIELD_URL_INVALID", "url", "Invalid URL.", {
      allowedProtocols: field.allowedProtocols,
    }));
  }

  if (field.type === "date") {
    issues.push(...validatePublicDateValue(value, field, path));
  }

  if (field.pattern && !matchesPublicRegex(value, field.pattern)) {
    issues.push(publicValidationIssue(path, "FIELD_PATTERN_INVALID", "pattern", "This field has an invalid format."));
  }

  for (const validator of publicScalarValidators(field)) {
    const issue = validatePublicFieldValidator(value, validator, path);
    if (issue) {
      issues.push(issue);
    }
  }

  return issues;
}

function validatePublicNumberValue(value: unknown, field: PublicScalarField, path: string): ValidationIssue[] {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return [publicValidationIssue(path, "FIELD_TYPE_INVALID", "type", "This field must be a number.")];
  }

  const issues: ValidationIssue[] = [];

  if (typeof field.min === "number" && value < field.min) {
    issues.push(publicValidationIssue(path, "FIELD_NUMBER_TOO_SMALL", "min", `Must be at least ${field.min}.`, {
      min: field.min,
    }));
  }

  if (typeof field.max === "number" && value > field.max) {
    issues.push(publicValidationIssue(path, "FIELD_NUMBER_TOO_LARGE", "max", `Must be at most ${field.max}.`, {
      max: field.max,
    }));
  }

  if (field.integer === true && !Number.isInteger(value)) {
    issues.push(publicValidationIssue(path, "FIELD_INTEGER_REQUIRED", "integer", "This number must be an integer."));
  }

  if (typeof field.maxDecimalPlaces === "number" && countDecimalPlaces(value) > field.maxDecimalPlaces) {
    issues.push(publicValidationIssue(path, "FIELD_DECIMAL_PLACES_INVALID", "maxDecimalPlaces", `Must have at most ${field.maxDecimalPlaces} decimal places.`, {
      maxDecimalPlaces: field.maxDecimalPlaces,
    }));
  }

  return issues;
}

function validatePublicBooleanValue(value: unknown, field: PublicScalarField, path: string): ValidationIssue[] {
  if (typeof value !== "boolean") {
    return [publicValidationIssue(path, "FIELD_TYPE_INVALID", "type", "This field must be true or false.")];
  }

  if (field.accepted === true && value !== true) {
    return [publicValidationIssue(path, "FIELD_ACCEPTED_REQUIRED", "accepted", "This field must be accepted.")];
  }

  return [];
}

function validatePublicCheckboxValue(value: unknown, field: PublicScalarField, path: string): ValidationIssue[] {
  if (!field.allowedValues) {
    return validatePublicBooleanValue(value, field, path);
  }

  if (!Array.isArray(value)) {
    return [publicValidationIssue(path, "FIELD_TYPE_INVALID", "type", "This field must be a list of selected values.")];
  }

  const issues: ValidationIssue[] = [];

  for (const entry of value) {
    if (typeof entry !== "string" || !field.allowedValues.includes(entry)) {
      issues.push(publicValidationIssue(path, "FIELD_VALUE_NOT_ALLOWED", "allowedValues", "This field contains an unsupported value.", {
        allowedValues: field.allowedValues,
      }));
      break;
    }
  }

  if (field.required === true && value.length === 0) {
    issues.push(publicValidationIssue(path, "FIELD_REQUIRED", "required", "This field is required."));
  }

  if (typeof field.minSelections === "number" && value.length < field.minSelections) {
    issues.push(publicValidationIssue(path, "FIELD_SELECTION_TOO_FEW", "minSelections", `Choose at least ${field.minSelections} options.`, {
      minSelections: field.minSelections,
    }));
  }

  if (typeof field.maxSelections === "number" && value.length > field.maxSelections) {
    issues.push(publicValidationIssue(path, "FIELD_SELECTION_TOO_MANY", "maxSelections", `Choose at most ${field.maxSelections} options.`, {
      maxSelections: field.maxSelections,
    }));
  }

  return issues;
}

function validatePublicContextualRules(
  schema: PublicSchemaInput,
  values: Record<string, unknown>,
  baseIssues: ValidationIssue[],
  selectedPaths: Set<string> | null,
  hiddenPaths: Set<string>,
): ValidationIssue[] {
  const rules = publicValidationRules(schema);
  if (rules.length === 0) {
    return [];
  }

  const issues: ValidationIssue[] = [];
  const baseErrors = groupValidationIssuesByPath(baseIssues);

  for (const rule of rules) {
    if (publicConditionHasErrors(rule.when, baseErrors) || publicConditionHasHiddenPath(rule.when, hiddenPaths)) {
      continue;
    }

    if (!safeEvaluatePublicCondition(rule.when, values)) {
      continue;
    }

    for (const action of rule.then) {
      if (
        !action.validator ||
        !publicValidationShouldVisit(action.path, selectedPaths, hiddenPaths)
      ) {
        continue;
      }

      const issue = validatePublicContextualValidator(action.path, action.validator, values, rule.id);
      if (issue) {
        issues.push(issue);
      }
    }
  }

  return issues;
}

function validatePublicContextualValidator(
  path: string,
  validator: PublicContextualValidator,
  values: Record<string, unknown>,
  ruleId: string,
): ValidationIssue | null {
  const value = resolveFormerieFieldValue(values, path);

  if (validator.type === "allOf" || validator.type === "anyOf") {
    if (validator.type === "anyOf" && isEmptyValue(value) && validator.validators.some((child) => child.type === "required")) {
      return publicValidationIssue(path, validator.code, validator.rule ?? "anyOf", validator.message, { ruleId });
    }

    const issues = validator.validators
      .map((child) => validatePublicContextualValidator(path, child, values, ruleId))
      .filter((issue): issue is ValidationIssue => issue !== null);

    if (validator.type === "allOf") {
      return issues[0] ?? null;
    }

    return issues.length < validator.validators.length
      ? null
      : publicValidationIssue(path, validator.code, validator.rule ?? "anyOf", validator.message, { ruleId });
  }

  if (validator.type === "required") {
    return isPublicRequiredValuePresent(value)
      ? null
      : publicValidationIssue(path, validator.code, validator.rule ?? "required", validator.message, { ruleId });
  }

  if (isEmptyValue(value)) {
    return null;
  }

  if (!isPublicFieldValidatorShape(validator)) {
    return null;
  }

  const issue = validatePublicFieldValidator(String(value), validator, path);
  return issue
    ? publicValidationIssue(path, issue.code, issue.rule, issue.message, { ruleId })
    : null;
}

function validatePublicFieldValidator(
  value: string,
  validator: PublicFieldValidator,
  path: string,
): ValidationIssue | null {
  let valid = true;

  switch (validator.type) {
    case "regex":
      if (typeof validator.pattern !== "string") {
        return null;
      }
      valid = matchesPublicRegex(value, validator.pattern, validator.flags);
      break;
    case "email":
      valid = EMAIL_PATTERN.test(value);
      break;
    case "url":
      valid = publicUrlIsValid(value, validator.allowedProtocols);
      break;
    case "domain":
      valid = DOMAIN_PATTERN.test(value);
      break;
    case "ip":
      valid = isIpv4(value) || isIpv6(value);
      break;
    case "ipv4":
      valid = isIpv4(value);
      break;
    case "ipv6":
      valid = isIpv6(value);
      break;
    case "uuid":
      valid = uuidPattern(validator.version).test(value);
      break;
    case "phoneE164":
      valid = E164_PHONE_PATTERN.test(value);
      break;
    case "countryCode":
      valid = /^[A-Z]{2}$/i.test(value);
      break;
    case "postalCode":
      valid = postalCodePattern(validator.country).test(value);
      break;
    case "regionCode":
      valid = regionCodes(validator.country).has(value.toUpperCase());
      break;
    case "startsWith":
      valid = typeof validator.value === "string" && value.startsWith(validator.value);
      break;
    case "endsWith":
      valid = typeof validator.value === "string" && value.endsWith(validator.value);
      break;
    case "contains":
      valid = typeof validator.value === "string" && value.includes(validator.value);
      break;
    case "notContains":
      valid = typeof validator.value === "string" && !value.includes(validator.value);
      break;
  }

  return valid
    ? null
    : publicValidationIssue(path, validator.code, validator.rule ?? validator.type, validator.message);
}

function visitSchemaNode(
  entries: PublicSchemaFieldEntry[],
  node: PublicSchemaNode,
  key: string,
  path: string,
  parentPath: string | undefined,
  depth: number,
  repeated: boolean,
): void {
  entries.push({
    path,
    key,
    parentPath,
    depth,
    repeated,
    node,
  });

  if (node.type === "object") {
    for (const [childKey, childNode] of Object.entries(node.fields)) {
      visitSchemaNode(entries, childNode, childKey, `${path}.${childKey}`, path, depth + 1, repeated);
    }
    return;
  }

  if (node.type === "array") {
    const itemPath = `${path}[]`;
    visitSchemaNode(entries, node.item, "[]", itemPath, path, depth + 1, true);
  }
}

function schemaFields(schema: PublicSchemaInput): Record<string, PublicSchemaNode> {
  const value = schema as { fields?: unknown; ok?: unknown };

  if (value.ok === true && isRecord(value.fields)) {
    return value.fields as Record<string, PublicSchemaNode>;
  }

  return schema as Record<string, PublicSchemaNode>;
}

function schemaVisibilityRules(schema: PublicSchemaInput): unknown[] {
  const value = schema as { visibilityRules?: unknown };

  return Array.isArray(value.visibilityRules)
    ? value.visibilityRules
    : [];
}

function isRuntimeVisibilityRule(value: unknown): value is PublicVisibilityRule {
  return (
    isRecord(value) &&
    typeof value.path === "string" &&
    isRecord(value.when) &&
    (value.default === undefined || value.default === "visible" || value.default === "hidden") &&
    (value.state === "visible" || value.state === "hidden")
  );
}

function safeEvaluatePublicCondition(condition: unknown, values: Record<string, unknown>): boolean {
  try {
    return evaluatePublicCondition(condition, values);
  } catch {
    return false;
  }
}

function evaluatePublicFieldCondition(
  condition: FieldCondition,
  values: Record<string, unknown>,
): boolean {
  const value = resolveFormerieFieldValue(values, condition.path);

  switch (condition.operator) {
    case "isFilled":
      return !isEmptyValue(value);
    case "isEmpty":
      return isEmptyValue(value);
    case "equals":
      return valuesEqual(value, condition.value);
    case "notEquals":
      return !valuesEqual(value, condition.value);
    case "in":
      return Array.isArray(condition.value) &&
        condition.value.some((entry) => valuesEqual(value, entry));
    case "notIn":
      return Array.isArray(condition.value) &&
        !condition.value.some((entry) => valuesEqual(value, entry));
    case "greaterThan":
      return compareValues(value, condition.value) > 0;
    case "greaterThanOrEqual":
      return compareValues(value, condition.value) >= 0;
    case "lessThan":
      return compareValues(value, condition.value) < 0;
    case "lessThanOrEqual":
      return compareValues(value, condition.value) <= 0;
    case "matches":
      return matchesConditionPattern(value, condition.value);
  }
}

function isFieldCondition(value: Record<string, unknown>): value is FieldCondition {
  return (
    typeof value.path === "string" &&
    value.path.trim().length > 0 &&
    typeof value.operator === "string" &&
    FIELD_CONDITION_OPERATORS.has(value.operator as FieldConditionOperator)
  );
}

function isPublicConditionExpression(value: unknown, depth = 0): boolean {
  if (!isRecord(value) || depth > 12) {
    return false;
  }

  if ("all" in value) {
    return Array.isArray(value.all) &&
      value.all.length > 0 &&
      value.all.every((child) => isPublicConditionExpression(child, depth + 1));
  }

  if ("any" in value) {
    return Array.isArray(value.any) &&
      value.any.length > 0 &&
      value.any.every((child) => isPublicConditionExpression(child, depth + 1));
  }

  if ("not" in value) {
    return isPublicConditionExpression(value.not, depth + 1);
  }

  return isFieldCondition(value);
}

function normalizePublicSchemaPath(path: string): string {
  return path.replace(/\[(?:\d+)?\]/g, "[]");
}

function isNormalizedPathHidden(path: string, hiddenPaths: Set<string>): boolean {
  const normalizedPath = normalizePublicSchemaPath(path);

  for (const hiddenPath of hiddenPaths) {
    if (
      normalizedPath === hiddenPath ||
      normalizedPath.startsWith(`${hiddenPath}.`) ||
      normalizedPath.startsWith(`${hiddenPath}[`)
    ) {
      return true;
    }
  }

  return false;
}

function schemaFieldForInput(
  schema: PublicSchemaInput | undefined,
  path: string,
): PublicSchemaNode | undefined {
  if (!schema) {
    return undefined;
  }

  const node = getPublicSchemaField(schema, path);
  return node?.type === "array" ? node.item : node;
}

function coerceFormDataEntry(
  value: FormDataEntryValue,
  schemaNode: PublicSchemaNode | undefined,
): unknown {
  const node = schemaNode?.type === "array" ? schemaNode.item : schemaNode;

  if (typeof value !== "string" || !node || node.type === "object" || node.type === "array") {
    return value;
  }

  return coerceScalarValue(value, node);
}

function coerceScalarValue(value: string, field: PublicScalarField): unknown {
  if (field.type === "number") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return value;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : value;
  }

  if (field.type === "boolean" || (field.type === "checkbox" && !field.allowedValues)) {
    return coerceBooleanValue(value);
  }

  return value;
}

function coerceBooleanValue(value: string): boolean | string {
  const normalized = value.trim().toLowerCase();

  if (normalized === "true" || normalized === "1" || normalized === "on" || normalized === "yes") {
    return true;
  }

  if (normalized === "false" || normalized === "0" || normalized === "off" || normalized === "no") {
    return false;
  }

  return value;
}

function applyMissingCheckboxValues(
  fields: Record<string, unknown>,
  options: FormerieFieldCollectionOptions,
): void {
  if (!options.schema || options.includeMissingCheckboxes === false) {
    return;
  }

  for (const entry of listPublicSchemaFields(options.schema)) {
    if (entry.repeated || !isScalarField(entry.node) || resolveFormerieFieldValue(fields, entry.path) !== undefined) {
      continue;
    }

    if (entry.node.type === "boolean") {
      setFormerieFieldValue(fields, entry.path, false);
    }

    if (entry.node.type === "checkbox") {
      setFormerieFieldValue(fields, entry.path, entry.node.allowedValues ? [] : false);
    }
  }
}

function isScalarField(node: PublicSchemaNode): node is PublicScalarField {
  return node.type !== "object" && node.type !== "array";
}

function publicPathTokens(path: string): Array<string | number | "*"> {
  const tokens: Array<string | number | "*"> = [];
  const pattern = /([^[.\]]+)|\[(\d*)\]/g;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(path)) !== null) {
    if (match[1]) {
      tokens.push(match[1]);
    } else if (match[2] === "") {
      tokens.push("*");
    } else if (match[2] !== undefined) {
      tokens.push(Number(match[2]));
    }
  }

  return tokens;
}

function resolvePathTokens(
  current: unknown,
  tokens: Array<string | number | "*">,
  index: number,
): unknown {
  if (index >= tokens.length) {
    return current;
  }

  const token = tokens[index];

  if (token === "*") {
    if (!Array.isArray(current)) {
      return undefined;
    }

    const values = current
      .map((entry) => resolvePathTokens(entry, tokens, index + 1))
      .filter((entry) => entry !== undefined);

    return values.length === 0 ? undefined : values;
  }

  if (Array.isArray(current) && typeof token === "number") {
    return resolvePathTokens(current[token], tokens, index + 1);
  }

  if (isRecord(current) && typeof token === "string") {
    return resolvePathTokens(current[token], tokens, index + 1);
  }

  return undefined;
}

function setPathTokenValue(
  fields: Record<string, unknown>,
  tokens: Array<string | number | "*">,
  value: unknown,
): void {
  let current: unknown = fields;

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    const nextToken = tokens[index + 1];
    const last = index === tokens.length - 1;

    if (last) {
      assignTokenValue(current, token, value);
      return;
    }

    current = ensureContainerForToken(current, token, nextToken);

    if (current === undefined) {
      return;
    }
  }
}

function ensureContainerForToken(
  current: unknown,
  token: string | number | "*",
  nextToken: string | number | "*" | undefined,
): unknown {
  const nextContainer = nextToken === "*" || typeof nextToken === "number" ? [] : {};

  if (isRecord(current) && typeof token === "string") {
    if (!isContainer(current[token])) {
      current[token] = nextContainer;
    }

    return current[token];
  }

  if (Array.isArray(current)) {
    const index = token === "*" ? current.length : token;
    if (typeof index !== "number") {
      return undefined;
    }

    if (!isContainer(current[index])) {
      current[index] = nextContainer;
    }

    return current[index];
  }

  return undefined;
}

function assignTokenValue(
  current: unknown,
  token: string | number | "*",
  value: unknown,
): void {
  if (isRecord(current) && typeof token === "string") {
    current[token] = mergeFormValue(current[token], value);
    return;
  }

  if (!Array.isArray(current)) {
    return;
  }

  const index = token === "*" ? current.length : token;
  if (typeof index !== "number") {
    return;
  }

  current[index] = mergeFormValue(current[index], value);
}

function mergeFormValue(current: unknown, next: unknown): unknown {
  if (current === undefined) {
    return next;
  }

  if (Array.isArray(current)) {
    return [...current, next];
  }

  return [current, next];
}

function isContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return isRecord(value) || Array.isArray(value);
}

function selectedPublicValidationPaths(
  schema: PublicSchemaInput,
  fields: Record<string, unknown>,
  options: ValidateFormeriePublicFieldsOptions,
): Set<string> | null {
  if (options.paths && options.paths.length > 0) {
    return new Set(options.paths.map(normalizePublicSchemaPath));
  }

  if (options.mode === "step" && options.step) {
    const step = publicSteps(schema).find((entry) => entry.id === options.step);
    return step ? new Set(step.fields.map(normalizePublicSchemaPath)) : new Set();
  }

  if (options.mode === "partial") {
    return new Set(Object.keys(fields).map(normalizePublicSchemaPath));
  }

  return null;
}

function publicValidationShouldVisit(
  path: string,
  selectedPaths: Set<string> | null,
  hiddenPaths: Set<string>,
): boolean {
  if (isNormalizedPathHidden(path, hiddenPaths)) {
    return false;
  }

  if (!selectedPaths) {
    return true;
  }

  const normalizedPath = normalizePublicSchemaPath(path);
  for (const selectedPath of selectedPaths) {
    if (
      normalizedPath === selectedPath ||
      normalizedPath.startsWith(`${selectedPath}.`) ||
      selectedPath.startsWith(`${normalizedPath}.`)
    ) {
      return true;
    }
  }

  return false;
}

function coercePublicValidationValue(value: unknown, field: PublicScalarField): unknown {
  if (field.type === "number" && typeof value === "string" && value.trim() !== "") {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }

  return value;
}

function validatePublicDateValue(value: string, field: PublicScalarField, path: string): ValidationIssue[] {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    return [publicValidationIssue(path, "FIELD_DATE_INVALID", "date", "Invalid date.")];
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return [publicValidationIssue(path, "FIELD_DATE_INVALID", "date", "Invalid date.")];
  }

  const issues: ValidationIssue[] = [];

  if (field.dateMin !== undefined && value < field.dateMin) {
    issues.push(publicValidationIssue(path, "FIELD_DATE_TOO_EARLY", "dateMin", `Must be on or after ${field.dateMin}.`, {
      dateMin: field.dateMin,
    }));
  }

  if (field.dateMax !== undefined && value > field.dateMax) {
    issues.push(publicValidationIssue(path, "FIELD_DATE_TOO_LATE", "dateMax", `Must be on or before ${field.dateMax}.`, {
      dateMax: field.dateMax,
    }));
  }

  return issues;
}

function publicScalarValidators(field: PublicScalarField): PublicFieldValidator[] {
  const validators = [...(field.validators ?? [])];

  for (const step of field.pipeline ?? []) {
    if (step.step === "validator" && step.validator) {
      validators.push(step.validator);
    }
  }

  return validators;
}

function publicValidationRules(schema: PublicSchemaInput): PublicValidationRule[] {
  const value = schema as { validationRules?: unknown };
  return Array.isArray(value.validationRules)
    ? value.validationRules.filter(isPublicValidationRule)
    : [];
}

function publicSteps(schema: PublicSchemaInput): Array<{ id: string; fields: string[] }> {
  const value = schema as { steps?: unknown };
  return Array.isArray(value.steps)
    ? value.steps.filter((step): step is { id: string; fields: string[] } =>
        isRecord(step) &&
        typeof step.id === "string" &&
        Array.isArray(step.fields) &&
        step.fields.every((field) => typeof field === "string")
      )
    : [];
}

function isPublicValidationRule(value: unknown): value is PublicValidationRule {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    isRecord(value.when) &&
    Array.isArray(value.then) &&
    value.then.every((action) =>
      isRecord(action) &&
      typeof action.path === "string" &&
      (action.validator === undefined || isPublicContextualValidator(action.validator))
    )
  );
}

function isPublicContextualValidator(value: unknown): value is PublicContextualValidator {
  if (!isRecord(value) || typeof value.type !== "string" || typeof value.code !== "string" || typeof value.message !== "string") {
    return false;
  }

  if (value.type === "allOf" || value.type === "anyOf") {
    return Array.isArray(value.validators) && value.validators.every(isPublicContextualValidator);
  }

  return value.type === "required" || value.type === "regex" || FIELD_VALIDATOR_TYPES.has(value.type);
}

function isPublicFieldValidatorShape(value: PublicContextualValidator): value is PublicFieldValidator {
  return value.type !== "allOf" && value.type !== "anyOf" && value.type !== "required";
}

const FIELD_VALIDATOR_TYPES = new Set([
  "contains",
  "countryCode",
  "domain",
  "email",
  "endsWith",
  "ip",
  "ipv4",
  "ipv6",
  "notContains",
  "phoneE164",
  "postalCode",
  "regionCode",
  "startsWith",
  "url",
  "uuid",
]);

function publicConditionHasErrors(condition: unknown, errors: Record<string, ValidationIssue[]>): boolean {
  return publicConditionPaths(condition).some((path) => (errors[path] ?? []).length > 0);
}

function publicConditionHasHiddenPath(condition: unknown, hiddenPaths: Set<string>): boolean {
  return publicConditionPaths(condition).some((path) => isNormalizedPathHidden(path, hiddenPaths));
}

function publicConditionPaths(condition: unknown): string[] {
  if (!isRecord(condition)) {
    return [];
  }

  const paths: string[] = [];
  if (typeof condition.path === "string") {
    paths.push(normalizePublicSchemaPath(condition.path));
  }

  for (const key of ["all", "any"] as const) {
    if (Array.isArray(condition[key])) {
      paths.push(...condition[key].flatMap(publicConditionPaths));
    }
  }

  if (isRecord(condition.not)) {
    paths.push(...publicConditionPaths(condition.not));
  }

  return paths;
}

function isPublicRequiredValuePresent(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : !isEmptyValue(value);
}

function publicUrlIsValid(value: string, allowedProtocols: string[] | undefined): boolean {
  try {
    const url = new URL(value);
    const protocols = (allowedProtocols ?? DEFAULT_URL_PROTOCOLS).map(normalizeProtocol);
    return protocols.includes(url.protocol.toLowerCase());
  } catch {
    return false;
  }
}

function normalizeProtocol(value: string): string {
  const protocol = value.trim().toLowerCase().replace(/:$/, "");
  return protocol ? `${protocol}:` : "";
}

function uuidPattern(version: "any" | "v4" | "v7" | undefined): RegExp {
  if (version === "v4") {
    return UUID_V4_PATTERN;
  }
  if (version === "v7") {
    return UUID_V7_PATTERN;
  }

  return UUID_ANY_PATTERN;
}

function postalCodePattern(country: "CA" | "US" | undefined): RegExp {
  return country === "US" ? US_POSTAL_CODE_PATTERN : CA_POSTAL_CODE_PATTERN;
}

function regionCodes(country: "CA" | "US" | undefined): Set<string> {
  return country === "US" ? US_REGION_CODES : CA_REGION_CODES;
}

function isIpv4(value: string): boolean {
  return IPV4_PATTERN.test(value);
}

function isIpv6(value: string): boolean {
  const normalized = value.startsWith("[") && value.endsWith("]") ? value.slice(1, -1) : value;

  try {
    const url = new URL(`http://[${normalized}]/`);
    return url.hostname.includes(":");
  } catch {
    return false;
  }
}

function countDecimalPlaces(value: number): number {
  if (!Number.isFinite(value) || Number.isInteger(value)) {
    return 0;
  }

  const [, decimal = ""] = String(value).split(".");
  return decimal.length;
}

function matchesPublicRegex(value: string, pattern: string, flags?: string): boolean {
  try {
    return new RegExp(pattern, flags).test(value);
  } catch {
    return false;
  }
}

function groupValidationIssuesByPath(issues: ValidationIssue[]): Record<string, ValidationIssue[]> {
  const grouped: Record<string, ValidationIssue[]> = {};

  for (const issue of issues) {
    grouped[issue.path] = [...(grouped[issue.path] ?? []), issue];
  }

  return grouped;
}

function publicValidationIssue(
  path: string,
  code: FieldErrorCode,
  rule: string,
  message: string,
  params?: Record<string, unknown>,
): ValidationIssue {
  return withoutUndefined({
    path,
    code,
    message,
    rule,
    params,
  });
}

function validationFailedErrorFrom(
  source: SubmitResponse | ValidateResponse | ValidationFailedError | unknown,
): ValidationFailedError | null {
  if (isValidationFailedErrorShape(source)) {
    return source;
  }

  if (isRecord(source) && source.ok === false && isValidationFailedErrorShape(source.error)) {
    return source.error;
  }

  return null;
}

function isValidationFailedErrorShape(value: unknown): value is ValidationFailedError {
  return (
    isRecord(value) &&
    value.code === "VALIDATION_FAILED" &&
    typeof value.message === "string" &&
    isRecord(value.fields) &&
    Array.isArray(value.issues)
  );
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  if (typeof left !== typeof right) {
    return false;
  }

  if (typeof left === "object" && left !== null && right !== null) {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  }

  return false;
}

function compareValues(left: unknown, right: unknown): number {
  const leftComparable = comparableValue(left);
  const rightComparable = comparableValue(right);

  if (leftComparable === null || rightComparable === null) {
    return Number.NaN;
  }

  return leftComparable - rightComparable;
}

function comparableValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }

    const timestamp = new Date(value).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
  }

  return null;
}

function matchesConditionPattern(value: unknown, pattern: unknown): boolean {
  if (typeof pattern !== "string" || isEmptyValue(value)) {
    return false;
  }

  try {
    return new RegExp(pattern).test(String(value));
  } catch {
    return false;
  }
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

function isHtmlFormElement(value: unknown): value is HTMLFormElement {
  return typeof HTMLFormElement !== "undefined" && value instanceof HTMLFormElement;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function withoutUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T;
}

function safeAttributeName(value: string | undefined, fallback: string): string {
  if (!value || !/^[a-zA-Z_:][\w:.-]*$/.test(value)) {
    return fallback;
  }

  return value;
}

function attributePresenceSelector(attribute: string): string {
  return `[${attribute}]`;
}

function attributeValueSelector(attribute: string, value: string): string {
  return `[${attribute}="${escapeAttributeValue(value)}"]`;
}

function escapeAttributeValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
}

function queryAll(root: ParentNode, selector: string): Element[] {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    return [];
  }
}
